/**
 * Materials — standalone screen that re-uses the Estimate's Materials pane
 * (same source of truth). "Advanced" toggle hides Adj / Waste% / Material Qty /
 * per-line Labour factor so Luke's daily view stays punchy (Phase 4).
 */
import { useEffect, useRef, useState, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import api, { fmtNZD } from "@/lib/api";
import StageSelect from "@/components/StageSelect";
import NumberInput from "@/components/NumberInput";
import MerchantImporterModal from "@/components/MerchantImporterModal";
import PlanAnalysisBlock from "@/components/PlanAnalysisBlock";

export default function MaterialsPage() {
  const { project } = useOutletContext();
  const [est, setEst] = useState(null);
  const [stages, setStages] = useState([]);
  const [factors, setFactors] = useState([]);
  const [importerOpen, setImporterOpen] = useState(false);
  const [advanced, setAdvanced] = useState(() => localStorage.getItem("lwco_materials_advanced") === "1");
  const [exceptionsOpen, setExceptionsOpen] = useState(true);
  const [confirmRule, setConfirmRule] = useState(null); // {item, factor} for rule-learning

  const saveTimer = useRef(null);
  const pending = useRef(null);

  async function load() {
    const [e, s, lf] = await Promise.all([
      api.get(`/projects/${project.id}/estimate`),
      api.get(`/projects/${project.id}/stages`).catch(() => ({ data: [] })),
      api.get(`/labour-factors`).catch(() => ({ data: [] })),
    ]);
    setEst(e.data); setStages(s.data || []); setFactors(lf.data || []);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [project.id]);

  function toggleAdvanced() {
    const next = !advanced;
    localStorage.setItem("lwco_materials_advanced", next ? "1" : "0");
    setAdvanced(next);
  }

  async function flush() {
    if (!pending.current) return;
    const data = pending.current; pending.current = null;
    const { data: res } = await api.put(`/projects/${project.id}/estimate`, { data });
    setEst(prev => prev ? { ...prev, totals: res.totals, net_cost: res.net_cost } : res);
  }
  function patch(fn) {
    setEst(prev => {
      const copy = { ...prev, data: JSON.parse(JSON.stringify(prev.data)) };
      fn(copy.data); pending.current = copy.data; return copy;
    });
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(flush, 500);
  }

  if (!est) return <div className="opacity-60">Loading materials…</div>;
  const data = est.data; const cats = data.materials || [];

  // Flatten lines once for the "exceptions" (unmatched / no stage) summary
  const flat = cats.flatMap((c, ci) => (c.items || []).map((it, ii) => ({ it, ci, ii, catName: c.category })));
  const unmatched = flat.filter(x => !(x.it.labour_factor_matched_id) && !(x.it.labour_factor_source === "manual"));
  const unstaged = flat.filter(x => !x.it.stage_id);

  return (
    <div className="space-y-3" data-testid="materials-page">
      <PlanAnalysisBlock projectId={project.id} onAdded={load}/>

      <div className="card flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <h3 className="text-silver">Materials</h3>
          <span className="text-xs opacity-60">{flat.length} line{flat.length===1?"":"s"} · {fmtNZD(est.totals?.materials_total)}</span>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs flex items-center gap-1" data-testid="toggle-advanced-row">
            <input type="checkbox" checked={advanced} onChange={toggleAdvanced} data-testid="chk-advanced"/>
            Advanced (Adj / Waste % / Material Qty / labour factor)
          </label>
          <button className="btn-silver" onClick={() => setImporterOpen(true)} data-testid="btn-open-importer">
            Import merchant CSV
          </button>
        </div>
      </div>
      {importerOpen && (
        <MerchantImporterModal projectId={project.id} onClose={() => setImporterOpen(false)}
          onImported={() => { setImporterOpen(false); load(); }}/>
      )}

      {(unmatched.length > 0 || unstaged.length > 0) && exceptionsOpen && (
        <div className="card border border-amber-400/60" data-testid="materials-exceptions">
          <div className="flex items-center justify-between gap-2">
            <h4 className="text-amber-300 text-sm font-bold">Exceptions to check</h4>
            <button className="text-xs opacity-70" onClick={() => setExceptionsOpen(false)}>Dismiss</button>
          </div>
          <ul className="text-xs mt-2 space-y-1">
            {unmatched.slice(0, 10).map((x, i) => (
              <li key={`u${i}`} data-testid={`exc-unmatched-${i}`}>
                ⚠ <span className="opacity-80">No labour factor matched:</span> <b>{x.it.desc}</b> ({x.it.qty} {x.it.unit})
              </li>
            ))}
            {unstaged.slice(0, 10).map((x, i) => (
              <li key={`s${i}`} data-testid={`exc-unstaged-${i}`}>
                ⚠ <span className="opacity-80">No stage selected:</span> <b>{x.it.desc}</b>
              </li>
            ))}
          </ul>
        </div>
      )}

      {cats.length === 0 && (
        <div className="card opacity-70 text-sm" data-testid="materials-empty">
          No material lines yet. Click <b>Import merchant CSV</b> above or add a category below.
        </div>
      )}

      {cats.map((c, ci) => (
        <div key={ci} className="card" data-testid={`mp-cat-${ci}`}>
          <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
            <input className="input max-w-xs" value={c.category}
                   onChange={e=>patch(d=>d.materials[ci].category = e.target.value)}
                   data-testid={`mp-cat-name-${ci}`}/>
            <button className="text-xs text-red-400" onClick={()=>patch(d=>d.materials.splice(ci,1))}>Delete category</button>
          </div>
          <div className="overflow-x-auto">
            <table className="table text-sm min-w-[560px]">
              <thead className="sticky top-0 bg-black z-10">
                <tr>
                  <th className="text-left">Product</th>
                  <th className="text-right">Qty</th>
                  <th>Unit</th>
                  <th className="text-right">Rate</th>
                  {advanced && <th className="text-right">Adj</th>}
                  {advanced && <th className="text-right">Waste %</th>}
                  {advanced && <th className="text-right">Material Qty</th>}
                  <th className="text-right">Total</th>
                  <th>Stage</th>
                  {advanced && <th>Labour factor</th>}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(c.items||[]).map((it, ii) => {
                  const materialQty = (parseFloat(it.qty||0)) * (1 + (parseFloat(it.waste_pct||0)/100)) * (parseFloat(it.adj||1));
                  return (
                    <tr key={it.id || ii} data-testid={`mp-row-${ci}-${ii}`}>
                      <td><input className="input" value={it.desc||""}
                                 onChange={e=>patch(d=>d.materials[ci].items[ii].desc=e.target.value)}/></td>
                      <td><NumberInput className="input text-right w-20" value={it.qty||0}
                             onChange={e=>patch(d=>d.materials[ci].items[ii].qty=parseFloat(e.target.value||0))}/></td>
                      <td><input className="input w-16" value={it.unit||"ea"}
                                 onChange={e=>patch(d=>d.materials[ci].items[ii].unit=e.target.value)}/></td>
                      <td><NumberInput className="input text-right w-24" value={it.rate||0}
                             onChange={e=>patch(d=>d.materials[ci].items[ii].rate=parseFloat(e.target.value||0))}/></td>
                      {advanced && <td><NumberInput className="input text-right w-16" value={it.adj ?? 1}
                             onChange={e=>patch(d=>d.materials[ci].items[ii].adj=parseFloat(e.target.value||1))}/></td>}
                      {advanced && <td><NumberInput className="input text-right w-16" value={it.waste_pct||0}
                             onChange={e=>patch(d=>d.materials[ci].items[ii].waste_pct=parseFloat(e.target.value||0))}/></td>}
                      {advanced && <td className="text-right text-silver">{materialQty.toFixed(2)}</td>}
                      <td className="text-right">{fmtNZD(it.total)}</td>
                      <td>
                        <StageSelect projectId={project.id} value={it.stage_id} stages={stages}
                          onStagesChanged={load}
                          onChange={(sid)=>patch(d=>{
                            d.materials[ci].items[ii].stage_id = sid || null;
                            d.materials[ci].items[ii].stage_name = sid ? (stages.find(s=>s.id===sid)?.name || "Unassigned") : "Unassigned";
                          })}
                          data-testid={`mp-stage-${ci}-${ii}`}/>
                      </td>
                      {advanced && (
                        <td className="text-xs">
                          {it.labour_factor_matched_id ? (
                            <span>
                              {it.labour_factor_trade} · {it.labour_factor_hours}h/{it.unit}
                              <span className={`ml-1 text-[9px] px-1 rounded ${
                                it.labour_factor_source === "manual" ? "bg-amber-500 text-black" : "bg-silver text-black"
                              }`}>{it.labour_factor_source}</span>
                            </span>
                          ) : <span className="opacity-50">—</span>}
                        </td>
                      )}
                      <td><button className="text-xs text-red-400" onClick={()=>patch(d=>d.materials[ci].items.splice(ii,1))}>Delete</button></td>
                    </tr>
                  );
                })}
                {(c.items||[]).length === 0 && (
                  <tr><td colSpan={advanced ? 11 : 8} className="opacity-50 text-center py-3">No lines yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <button className="btn-silver text-xs mt-2"
            onClick={()=>patch(d=>{ if(!d.materials[ci].items) d.materials[ci].items=[];
              d.materials[ci].items.push({id:(crypto.randomUUID?.()||String(Date.now())),desc:"",qty:0,unit:"ea",rate:0,waste_pct:0,adj:1}); })}>
            + Add line
          </button>
        </div>
      ))}

      <button className="btn-ghost text-sm" data-testid="btn-add-cat"
        onClick={()=>patch(d=>{d.materials=d.materials||[]; d.materials.push({category:"New category", items:[]});})}>
        + Add category
      </button>
    </div>
  );
}
